import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { Chess } from "chess.js";

export const CHESS_BOTS = [
  { key: "pino", name: "Nonno Pino", avatar: "🧓", rating: 600, level: "Principiante", depth: 1, noise: 3, coins: 20, desc: "Gioca per divertirsi e ogni tanto si distrae" },
  { key: "sofia", name: "Sofia", avatar: "👧", rating: 900, level: "Facile", depth: 1, noise: 0.6, coins: 35, desc: "Conosce le regole e mangia quando può" },
  { key: "marco", name: "Marco", avatar: "🧑‍🎓", rating: 1200, level: "Medio", depth: 2, noise: 0.3, coins: 50, desc: "Pensa alla mossa successiva dell'avversario" },
  { key: "aisha", name: "Aisha", avatar: "👩‍🏫", rating: 1500, level: "Difficile", depth: 3, noise: 0.15, coins: 70, desc: "Calcola diverse mosse in anticipo" },
  { key: "leo", name: "Gran Maestro Leo", avatar: "🦁", rating: 1800, level: "Esperto", depth: 3, noise: 0, coins: 100, desc: "Non perdona gli errori" },
];

const PIECES = { w: { k: "♔", q: "♕", r: "♖", b: "♗", n: "♘", p: "♙" }, b: { k: "♚", q: "♛", r: "♜", b: "♝", n: "♞", p: "♟" } };
const VAL = { p: 100, n: 320, b: 330, r: 500, q: 900, k: 0 };
const FILES = "abcdefgh";

const centerBonus = (sq) => {
  if (sq.type !== "p" && sq.type !== "n") return 0;
  const f = FILES.indexOf(sq.square[0]), r = Number(sq.square[1]) - 1;
  const dist = Math.max(Math.abs(f - 3.5), Math.abs(r - 3.5));
  return Math.round((3.5 - dist) * 6);
};

// score from Black's (AI) point of view
const evaluate = (game, depth) => {
  if (game.isCheckmate()) return game.turn() === "w" ? 100000 + depth : -100000 - depth;
  if (game.isDraw()) return 0;
  let s = 0;
  for (const row of game.board()) for (const sq of row) if (sq) {
    const v = VAL[sq.type] + centerBonus(sq);
    s += sq.color === "b" ? v : -v;
  }
  return s;
};

const orderMoves = (moves) => moves.sort((a, b) => ((b.captured ? VAL[b.captured] : 0) + (b.promotion ? 800 : 0)) - ((a.captured ? VAL[a.captured] : 0) + (a.promotion ? 800 : 0)));

const search = (game, depth, alpha, beta, maximizing) => {
  if (depth === 0 || game.isGameOver()) return evaluate(game, depth);
  const moves = orderMoves(game.moves({ verbose: true }));
  let best = maximizing ? -Infinity : Infinity;
  for (const m of moves) {
    game.move({ from: m.from, to: m.to, promotion: m.promotion });
    const v = search(game, depth - 1, alpha, beta, !maximizing);
    game.undo();
    if (maximizing) { best = Math.max(best, v); alpha = Math.max(alpha, v); }
    else { best = Math.min(best, v); beta = Math.min(beta, v); }
    if (beta <= alpha) break;
  }
  return best;
};

const bestMove = (game, bot) => {
  const moves = orderMoves(game.moves({ verbose: true }));
  let best = null, bestV = -Infinity;
  for (const m of moves) {
    game.move({ from: m.from, to: m.to, promotion: m.promotion });
    let v = search(game, bot.depth - 1, -Infinity, Infinity, false);
    game.undo();
    v += (Math.random() - 0.5) * 2 * bot.noise * 100;
    if (v > bestV) { bestV = v; best = m; }
  }
  return best;
};

const RULES = [
  ["🎯 Obiettivo", "Dare scacco matto al Re avversario: attaccarlo in modo che non possa più sfuggire."],
  ["♙ Pedone", "Avanza di 1 casella (2 alla prima mossa), mangia in diagonale. Arrivato in fondo diventa Regina."],
  ["♖ Torre", "Si muove in orizzontale e verticale, quante caselle vuole."],
  ["♗ Alfiere", "Si muove in diagonale, quante caselle vuole."],
  ["♘ Cavallo", "Salta a \"L\" (2+1 caselle) e può passare sopra gli altri pezzi."],
  ["♕ Regina", "Il pezzo più forte: unisce i movimenti di Torre e Alfiere."],
  ["♔ Re", "Si muove di 1 casella in ogni direzione. Non può mai mettersi sotto attacco."],
  ["🏰 Arrocco", "Re e Torre si muovono insieme una volta sola: tocca il Re e spostalo di 2 caselle."],
  ["⚡ En passant", "Mossa speciale: un pedone può catturare il pedone avversario appena avanzato di 2 caselle."],
  ["🤝 Patta", "Se nessuno può vincere (stallo, ripetizione, pochi pezzi) la partita finisce pari."],
];

function RulesPanel() {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-white/10 rounded-2xl p-3 text-white mb-4" data-testid="chess-rules">
      <button onClick={() => setOpen(!open)} className="w-full flex justify-between items-center font-bold" data-testid="chess-rules-toggle">
        <span>📖 Come si gioca</span><span>{open ? "▲" : "▼"}</span>
      </button>
      {open && (
        <ul className="mt-3 space-y-2 text-sm" data-testid="chess-rules-list">
          {RULES.map(([t, d]) => <li key={t}><b>{t}</b> — <span className="text-white/80">{d}</span></li>)}
          <li className="text-white/60 text-xs pt-1">Tocca un pezzo per vedere le mosse possibili (puntini verdi), poi tocca dove vuoi andare. Le mosse illegali non sono permesse.</li>
        </ul>
      )}
    </div>
  );
}

export function BotList({ bots = CHESS_BOTS, rating, onPick, testPrefix = "chess" }) {
  const recIdx = useMemo(() => {
    let best = 0;
    bots.forEach((b, i) => { if (Math.abs(b.rating - rating) < Math.abs(bots[best].rating - rating)) best = i; });
    return best;
  }, [rating, bots]);
  return (
    <div className="space-y-2 mb-4" data-testid={`${testPrefix}-bot-list`}>
      {bots.map((b, i) => {
        const tag = i === recIdx ? "⭐ Consigliato per te" : i < recIdx ? "Più facile" : "Più difficile";
        return (
          <button key={b.key} onClick={() => onPick(b)} data-testid={`${testPrefix}-bot-${b.key}`}
            className={`w-full text-left rounded-xl p-3 flex items-center gap-3 border-2 transition-colors ${i === recIdx ? "bg-amber-50 border-amber-400" : "bg-white border-gray-200 hover:border-indigo-300"}`}>
            <span className="text-3xl">{b.avatar}</span>
            <span className="flex-1">
              <span className="block font-bold text-gray-800">{b.name} <span className="text-xs font-normal text-gray-500">· {b.level} · {b.rating}</span></span>
              <span className="block text-xs text-gray-500">{b.desc}</span>
            </span>
            <span className="text-right">
              <span className={`block text-xs font-bold ${i === recIdx ? "text-amber-600" : i < recIdx ? "text-green-600" : "text-red-500"}`}>{tag}</span>
              <span className="block text-xs text-yellow-600 font-bold">+{b.coins} 💰</span>
            </span>
          </button>
        );
      })}
    </div>
  );
}

export default function ChessGame({ t, onBack, playSound, child, others = [], onFinish, onVersusFinish }) {
  const gameRef = useRef(new Chess());
  const game = gameRef.current;
  const [mode, setMode] = useState(null); // "bot" | "versus" | "local"
  const [bot, setBot] = useState(null);
  const [opponent, setOpponent] = useState(null);
  const [picking, setPicking] = useState(false);
  const [fen, setFen] = useState(game.fen());
  const [selected, setSelected] = useState(null);
  const [last, setLast] = useState(null);
  const [over, setOver] = useState(null); // {result:"w"|"b"|"draw", reason}
  const [reward, setReward] = useState(null);
  const [thinking, setThinking] = useState(false);

  const rating = child?.chess_rating ?? 800;
  const turn = game.turn();
  const whiteName = mode === "versus" ? child?.name : mode === "local" ? "Bianco" : child?.name || "Tu";
  const blackName = mode === "bot" ? `${bot?.avatar} ${bot?.name}` : mode === "versus" ? opponent?.name : "Nero";

  const start = (m, b, opp) => {
    gameRef.current = new Chess();
    setFen(gameRef.current.fen());
    setMode(m); setBot(b || null); setOpponent(opp || null);
    setSelected(null); setLast(null); setOver(null); setReward(null); setThinking(false);
  };

  const finish = useCallback(async (result, reason) => {
    setOver({ result, reason });
    const playerWon = result === "w";
    if (playSound) playSound(result === "draw" ? "click" : playerWon || mode !== "bot" ? "victory" : "wrong");
    if (mode === "bot" && onFinish) {
      const res = await onFinish(playerWon ? "win" : result === "draw" ? "draw" : "loss", bot.key);
      if (res) setReward(res);
    } else if (mode === "versus" && onVersusFinish && opponent) {
      const draw = result === "draw";
      const winnerId = result === "b" ? opponent.id : child.id;
      const loserId = result === "b" ? child.id : opponent.id;
      const res = await onVersusFinish(winnerId, loserId, draw);
      if (res) setReward({ text: draw ? `Patta: +${res.coins_winner} 💰 a testa` : `${result === "b" ? opponent.name : child.name} +${res.coins_winner} 💰 · ${result === "b" ? child.name : opponent.name} +${res.coins_loser} 💰` });
    }
  }, [mode, bot, opponent, child, onFinish, onVersusFinish, playSound]);

  const doMove = useCallback((m) => {
    const g = gameRef.current;
    const res = g.move({ from: m.from, to: m.to, promotion: m.promotion ? "q" : undefined });
    setFen(g.fen()); setLast([m.from, m.to]); setSelected(null);
    if (playSound) playSound(res.captured ? "coins" : "click");
    if (g.isCheckmate()) finish(g.turn() === "w" ? "b" : "w", "Scacco matto");
    else if (g.isDraw()) finish("draw", g.isStalemate() ? "Stallo" : g.isThreefoldRepetition() ? "Ripetizione" : g.isInsufficientMaterial() ? "Pezzi insufficienti" : "Regola delle 50 mosse");
  }, [finish, playSound]);

  useEffect(() => {
    if (mode !== "bot" || turn !== "b" || over) return;
    setThinking(true);
    const timer = setTimeout(() => {
      const m = bestMove(gameRef.current, bot);
      setThinking(false);
      if (m) doMove(m);
    }, 400);
    return () => clearTimeout(timer);
  }, [mode, turn, over, fen, bot, doMove]);

  const legal = useMemo(() => (selected ? game.moves({ square: selected, verbose: true }) : []), [selected, game, fen]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSquare = (sq) => {
    if (over || thinking || (mode === "bot" && turn === "b")) return;
    const mv = legal.find((m) => m.to === sq);
    if (mv) { doMove(mv); return; }
    const piece = game.get(sq);
    setSelected(piece && piece.color === turn ? sq : null);
  };

  const resign = () => {
    if (over) return;
    const who = turn === "w" ? whiteName : blackName;
    if (mode !== "bot" && !window.confirm(`${who} abbandona la partita?`)) return;
    finish(turn === "w" ? "b" : "w", "Abbandono");
  };

  const inCheck = game.inCheck();
  const kingSq = inCheck ? game.board().flat().find((s) => s && s.type === "k" && s.color === turn)?.square : null;

  if (!mode) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-emerald-900 p-4" data-testid="chess-menu">
        <div className="max-w-md mx-auto">
          <button onClick={onBack} className="bg-white/20 text-white px-4 py-2 rounded-full font-bold hover:bg-white/30 mb-4" data-testid="chess-back-btn">← {t.backHome}</button>
          <div className="text-center text-white mb-4">
            <div className="text-6xl mb-2">♔♚</div>
            <h2 className="text-3xl font-bold">Scacchi</h2>
            <p className="text-white/70 text-sm mt-1" data-testid="chess-rating">Il tuo punteggio scacchi: <b className="text-amber-300">{rating}</b></p>
          </div>
          <RulesPanel />
          <div className="bg-white rounded-3xl p-4 shadow-2xl">
            <h3 className="font-bold text-gray-800 mb-2">🤖 Sfida un avversario</h3>
            <p className="text-xs text-gray-500 mb-3">Gli avversari sono ordinati dal più facile al più difficile. Vinci per far salire il tuo punteggio!</p>
            <BotList rating={rating} onPick={(b) => start("bot", b)} />
            {others.length > 0 && (
              <button onClick={() => setPicking(true)} className="w-full py-3 mb-2 rounded-xl bg-gradient-to-r from-purple-600 to-pink-500 text-white font-bold hover:opacity-90" data-testid="chess-vs-friend-btn">⚔️ Sfida un Amico</button>
            )}
            <button onClick={() => start("local")} className="w-full py-3 rounded-xl bg-gradient-to-r from-slate-600 to-slate-700 text-white font-bold hover:opacity-90" data-testid="chess-two-players-btn">👥 2 Giocatori</button>
          </div>
          {picking && (
            <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50" data-testid="chess-opponent-picker">
              <div className="bg-white rounded-3xl p-6 max-w-sm w-full max-h-[80vh] overflow-y-auto">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-xl font-bold text-gray-800">⚔️ Chi sfidi?</h3>
                  <button onClick={() => setPicking(false)} className="text-gray-400 text-2xl" data-testid="chess-close-picker">✕</button>
                </div>
                <div className="space-y-2">
                  {others.map((o) => (
                    <button key={o.id} onClick={() => { setPicking(false); start("versus", null, o); }} data-testid={`chess-opponent-${o.id}`}
                      className="w-full flex items-center gap-3 bg-purple-50 hover:bg-purple-100 border-2 border-purple-200 rounded-xl p-3 font-bold text-gray-700">
                      <span className="text-3xl">{o.avatar}</span><span>{o.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  const board = game.board();
  const playerLost = over && over.result !== "draw" && ((mode === "bot" && over.result === "b"));
  const status = over
    ? over.result === "draw" ? `🤝 Patta (${over.reason})` : `${playerLost ? "😮" : "🎉"} ${over.reason}! Vince ${over.result === "w" ? "♔ " + whiteName : "♚ " + blackName}`
    : thinking ? `${bot.avatar} ${bot.name} pensa... 🤔` : `Tocca a ${turn === "w" ? "♔ " + whiteName : "♚ " + blackName}${inCheck ? " — ⚠️ Scacco!" : ""}`;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-emerald-900 p-4" data-testid="chess-game">
      <div className="max-w-lg mx-auto">
        <button onClick={() => setMode(null)} className="bg-white/20 text-white px-4 py-2 rounded-full font-bold hover:bg-white/30 mb-3" data-testid="chess-back-btn">← Scacchi</button>
        <div className="text-center mb-3">
          <div className={`inline-block rounded-2xl px-4 py-2 font-bold ${over ? "bg-white text-slate-800" : "text-white"}`} data-testid="chess-status">{status}</div>
          {reward && <div className="text-amber-300 font-bold mt-1" data-testid="chess-reward">{reward.text || `+${reward.coins_earned} 💰 · Punteggio ${reward.rating_after} (${reward.rating_delta >= 0 ? "+" : ""}${reward.rating_delta})`}</div>}
        </div>
        <div className="grid grid-cols-8 rounded-xl overflow-hidden shadow-2xl mx-auto border-4 border-slate-950" style={{ maxWidth: 440 }} data-testid="chess-board">
          {board.map((row, r) => row.map((sq, c) => {
            const name = FILES[c] + (8 - r);
            const dark = (r + c) % 2 === 1;
            const isSel = selected === name;
            const target = legal.find((m) => m.to === name);
            const isLast = last && (last[0] === name || last[1] === name);
            return (
              <div key={name} onClick={() => onSquare(name)} data-testid={`chess-sq-${name}`}
                className={`aspect-square relative flex items-center justify-center cursor-pointer select-none text-3xl sm:text-4xl leading-none
                  ${dark ? "bg-emerald-700" : "bg-emerald-100"} ${isLast ? "ring-2 ring-inset ring-yellow-300" : ""} ${isSel ? "ring-4 ring-inset ring-amber-400" : ""} ${kingSq === name ? "bg-red-500" : ""}`}>
                {sq && <span className={sq.color === "w" ? "text-white drop-shadow-[0_2px_2px_rgba(0,0,0,0.8)]" : "text-slate-900 drop-shadow-[0_1px_1px_rgba(255,255,255,0.4)]"}>{PIECES[sq.color][sq.type]}</span>}
                {target && <span className={`absolute ${sq ? "inset-0 border-4 border-green-400/80 rounded-sm" : "w-3 h-3 rounded-full bg-green-400/90"}`}></span>}
              </div>
            );
          }))}
        </div>
        <div className="flex gap-3 mt-4">
          {over ? (
            <button onClick={() => start(mode, bot, opponent)} className="flex-1 py-3 rounded-xl bg-white text-slate-800 font-bold hover:bg-gray-100" data-testid="chess-restart-btn">🔄 Gioca ancora</button>
          ) : (
            <button onClick={resign} className="flex-1 py-3 rounded-xl bg-white/15 text-white font-bold hover:bg-white/25" data-testid="chess-resign-btn">🏳️ Abbandona</button>
          )}
        </div>
        <p className="text-white/60 text-xs text-center mt-3">Tocca un pezzo per vedere le mosse. Il pedone in fondo diventa Regina. Scacco matto per vincere!</p>
      </div>
    </div>
  );
}
